import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Undo2, PenLine, Plus, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHead } from "@/components/brand/Card";
import { TruncatedCell } from "@/components/brand/TruncatedCell";
import { CellEditor } from "@/components/admin/CellEditor";
import { RecordEditDialog } from "@/components/admin/RecordEditDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  useReferenceDataRecords,
  useReferenceDataSchema,
  useStageReferenceDataEdits,
} from "@/hooks/useApi";
import {
  RecordsSearchBar,
  type RecordsSearch,
  type RecordsSearchBarHandle,
} from "@/components/admin/reference-data/RecordsSearchBar";
import { RecordViolations } from "@/components/admin/reference-data/RecordViolations";
import { toInputString } from "@/lib/reference-data-cell";
import { buildEditOperations, errMessage, recordViolations } from "@/lib/reference-data-edit";
import { PAGE_SIZE } from "@/components/admin/reference-data/shared";
import { RecordRollbackDialog } from "@/components/admin/reference-data/dialogs";

// ---------------------------------------------------------------------------
// Records tab — paginated active rows, with in-place create/edit staging
// ---------------------------------------------------------------------------

interface RecordsTabProps {
  tableName: string;
  /** Título do cartão; sem ele vale "Registros". */
  title?: string;
  onStaged?: () => void;
}

export function RecordsTab(props: RecordsTabProps) {
  // Trocar a tabela reinicia busca, página e rascunhos antes de qualquer leitura da nova tabela.
  return <RecordsTabContent key={props.tableName} {...props} />;
}

function RecordsTabContent({ tableName, title, onStaged }: RecordsTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState<RecordsSearch>({ term: "" });
  const searchBar = useRef<RecordsSearchBarHandle>(null);
  const q = search.term;
  const column = search.column;
  // A página pertence ao par aplicado; voltar a um par anterior não restaura a página antiga.
  const [paging, setPaging] = useState<RecordsSearch & { page: number }>({ term: "", page: 0 });
  const sameSearch = paging.term === q && paging.column === column;
  const page = sameSearch ? paging.page : 0;
  if (!sameSearch) setPaging({ term: q, column, page: 0 });
  const setPage = (next: (current: number) => number) => setPaging({ term: q, column, page: next(page) });
  const { data, isLoading, isFetching, isPlaceholderData } = useReferenceDataRecords(tableName, {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    q: q || undefined,
    column,
  });
  const { data: schema } = useReferenceDataSchema(tableName);
  const stageMutation = useStageReferenceDataEdits(tableName);

  // Staged edits (not yet sent): existing-row id → changed columns, plus a list
  // of brand-new rows. Mirrors the CatalogView staged-map + bulk-save pattern.
  const [editing, setEditing] = useState(false);
  const [violations, setViolations] = useState<string[]>([]);
  // "Batch edit" clicado com busca pendente: a busca já foi aplicada, mas a edição só abre quando
  // a leitura filtrada chega. Com placeholderData a grade ainda é a da busca anterior, e abrir a
  // edição nela trocaria as linhas por baixo do que a pessoa digita.
  const [startingEdit, setStartingEdit] = useState(false);
  const searchSettled = !isLoading && !isPlaceholderData;
  if (startingEdit && searchSettled) {
    setStartingEdit(false);
    setEditing(true);
  }
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});
  const [newRows, setNewRows] = useState<Array<{ tempId: string; values: Record<string, unknown> }>>(
    [],
  );
  const tempIdRef = useRef(0);

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Editable = the schema's business columns; `version` is bookkeeping (shown,
  // never edited). Fall back to the row's own keys when there is no schema.
  const editableColumns = useMemo(() => schema?.columns.map((c) => c.name) ?? [], [schema?.columns]);
  const editableSet = useMemo(() => new Set(editableColumns), [editableColumns]);
  const requiredSet = useMemo(
    () => new Set((schema?.columns ?? []).filter((c) => !c.is_nullable).map((c) => c.name)),
    [schema?.columns],
  );
  // The full column metadata, by name — `allowed_values` decides select vs input.
  const columnByName = useMemo(
    () => new Map((schema?.columns ?? []).map((c) => [c.name, c])),
    [schema?.columns],
  );
  const columns = useMemo(() => {
    if (editableColumns.length) return [...editableColumns, "version"];
    if (items.length) return Object.keys(items[0]);
    return [];
  }, [editableColumns, items]);

  const dirtyCount =
    Object.values(edits).filter((v) => Object.keys(v).length > 0).length + newRows.length;
  const hasChanges = dirtyCount > 0;

  // Row whose "revert to an earlier version" dialog is open (S-046).
  const [revertTarget, setRevertTarget] = useState<string | null>(null);
  // Row whose edit dialog is open. Clicking a row is the quick path for a single
  // record; the batch grid owns the cells while `editing`, so it wins there.
  const [editRow, setEditRow] = useState<Record<string, unknown> | null>(null);
  const rowClickable = !editing && editableColumns.length > 0;
  // +1 for the trailing action column, which only renders outside edit mode.
  const colSpan = Math.max(columns.length, 1) + (editing ? 0 : 1);

  const rowIdOf = (row: Record<string, unknown>) => String(row.id ?? "");
  const cellValue = (row: Record<string, unknown>, col: string): unknown => {
    const staged = edits[rowIdOf(row)];
    return staged && col in staged ? staged[col] : row[col];
  };
  const setCell = (id: string, col: string, value: string) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), [col]: value } }));
  const setNewCell = (tempId: string, col: string, value: string) =>
    setNewRows((prev) =>
      prev.map((r) => (r.tempId === tempId ? { ...r, values: { ...r.values, [col]: value } } : r)),
    );
  const addRow = () => {
    tempIdRef.current += 1;
    setNewRows((prev) => [...prev, { tempId: `new-${tempIdRef.current}`, values: {} }]);
  };
  const cancelEditing = () => {
    setViolations([]);
    setEdits({});
    setNewRows([]);
    setEditing(false);
  };

  const handleStage = async () => {
    const operations = buildEditOperations(edits, newRows);
    if (!operations.length) {
      toast.error(t("Nothing to stage yet — edit a cell or fill in a new row."));
      return;
    }
    setViolations([]);
    try {
      const res = await stageMutation.mutateAsync(operations);
      toast.success(
        t("Staged {{n}} change(s) as a draft — promote it in Versions & audit.", {
          n: res.report?.row_count ?? operations.length,
        }),
      );
      cancelEditing();
      onStaged?.();
    } catch (err) {
      const rejected = recordViolations(err);
      if (rejected.length) {
        setViolations(rejected);
      } else {
        toast.error(errMessage(err, t("Could not stage the edits.")));
      }
    }
  };

  return (
    <Card className="[&>div:first-child]:flex-wrap [&>div:first-child>div:last-child]:min-w-0 [&>div:first-child>div:last-child]:max-w-full">
      <CardHead
        overline={t("ACTIVE VERSION")}
        title={title ?? t("Records")}
        right={
          <div className="flex min-w-0 flex-wrap items-end gap-3">
            <RecordsSearchBar
              ref={searchBar}
              columns={schema?.columns ?? []}
              disabled={editing || startingEdit}
              onSearch={setSearch}
            />
            <span className="font-mono text-[11px] text-[#7b8799]">
              {isFetching && <Loader2 className="inline w-3 h-3 mr-1.5 animate-spin" />}
              {t("{{total}} rows · actives", { total: total.toLocaleString() })}
            </span>
            {editing ? (
              <div className="flex items-center gap-1.5">
                {hasChanges && (
                  <span className="font-mono text-[11px] text-[#c25500]">
                    {t("{{n}} unsaved", { n: dirtyCount })}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={addRow}
                  disabled={stageMutation.isPending}
                >
                  <Plus className="w-3.5 h-3.5" /> {t("Add row")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEditing}
                  disabled={stageMutation.isPending}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  variant="action"
                  size="sm"
                  className="gap-1.5"
                  onClick={handleStage}
                  disabled={!hasChanges || stageMutation.isPending}
                >
                  {stageMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <PenLine className="w-3.5 h-3.5" />
                  )}
                  {t("Stage edits")}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  // Aplica a busca pendente agora: a grade não pode trocar enquanto se edita.
                  searchBar.current?.commitSearch();
                  setStartingEdit(true);
                }}
                disabled={isLoading || startingEdit || editableColumns.length === 0}
              >
                {startingEdit ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <PenLine className="w-3.5 h-3.5" />
                )}{" "}
                {t("Batch edit")}
              </Button>
            )}
          </div>
        }
      />
      {violations.length > 0 && (
        <div className="p-4"><RecordViolations violations={violations} /></div>
      )}
      {/* One provider for the whole grid: TruncatedCell renders a trigger per
          overflowing cell and must not create one of these each time. The app-wide
          provider (App.tsx) uses the default 700ms delay — too slow for "hover a
          cell to read it", so this one mirrors the 150ms of ColumnHint. */}
      <TooltipProvider delayDuration={150}>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                {columns.map((c) => {
                  const allowed = columnByName.get(c)?.allowed_values;
                  return (
                    <th key={c} className="whitespace-nowrap">
                      {c}
                      {editing && requiredSet.has(c) && <span className="text-[#c25500]"> *</span>}
                      {/* Restricted columns are flagged in read-only mode too, so the
                          accepted values are known before clicking Edit. */}
                      {allowed && allowed.length > 0 && (
                        <span
                          className="inline-flex align-middle ml-1 cursor-help text-[#7b8799]"
                          title={`${t("Accepted values")}: ${allowed.join(", ")}`}
                        >
                          <Info className="w-3 h-3" />
                        </span>
                      )}
                    </th>
                  );
                })}
                {/* Per-row revert (S-046). Hidden while batch-editing: staging an
                    edit and reverting a row are opposite intents. */}
                {!editing && <th className="w-px" />}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={colSpan} className="py-10 text-center text-[#7b8799]">
                    <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                    {t("Loading rows…")}
                  </td>
                </tr>
              ) : items.length === 0 && newRows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="py-12 text-center text-[#7b8799] text-sm">
                    {q ? t("No rows match the search.") : t("This source has no active rows.")}
                  </td>
                </tr>
              ) : (
                <>
                  {/* New rows render FIRST so they're fillable without scrolling. */}
                  {editing &&
                    newRows.map((nr) => (
                      <tr key={nr.tempId} className="bg-[#fffdf5]">
                        {columns.map((c) => (
                          <td key={c} className="mono text-[12px] whitespace-nowrap max-w-[320px]">
                            {editableSet.has(c) ? (
                              <CellEditor
                                column={columnByName.get(c)}
                                label={`new ${c}`}
                                className="w-full min-w-[120px] rounded px-1.5 py-0.5 text-[12px] outline-none border border-amber-400 bg-white focus:border-[#08254c]"
                                value={toInputString(nr.values[c])}
                                placeholder={requiredSet.has(c) ? t("required") : ""}
                                onChange={(value) => setNewCell(nr.tempId, c, value)}
                              />
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  {items.map((row, i) => {
                    const id = rowIdOf(row) || String(i);
                    return (
                      <tr
                        key={id}
                        {...(rowClickable && {
                          role: "button",
                          tabIndex: 0,
                          "aria-label": t("Edit this record"),
                          className: "cursor-pointer hover:bg-[#fafbfc]",
                          onClick: () => setEditRow(row),
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key !== "Enter" && e.key !== " ") return;
                            e.preventDefault(); // Space would scroll the page.
                            setEditRow(row);
                          },
                        })}
                      >
                        {columns.map((c) => {
                          const editableCell = editing && editableSet.has(c);
                          const dirty = !!edits[id] && c in edits[id];
                          return (
                            <td key={c} className="mono text-[12px] whitespace-nowrap max-w-[320px]">
                              {editableCell ? (
                                <CellEditor
                                  column={columnByName.get(c)}
                                  label={c}
                                  className={`w-full min-w-[120px] rounded px-1.5 py-0.5 text-[12px] outline-none border ${
                                    dirty
                                      ? "border-amber-400 bg-[#fffdf5]"
                                      : "border-[#e8ebf0] focus:border-[#08254c]"
                                  }`}
                                  value={toInputString(cellValue(row, c))}
                                  placeholder=""
                                  onChange={(value) => setCell(id, c, value)}
                                />
                              ) : (
                                <TruncatedCell value={row[c]} />
                              )}
                            </td>
                          );
                        })}
                        {!editing && (
                          // Reverting and editing are opposite intents: keep the
                          // row click from firing underneath the button.
                          <td className="pr-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1.5 whitespace-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRevertTarget(id);
                              }}
                              title={t("Revert this row to an earlier version")}
                            >
                              <Undo2 className="w-3.5 h-3.5" />
                              {t("Revert")}
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </TooltipProvider>
      <RecordRollbackDialog
        tableName={tableName}
        recordId={revertTarget}
        onOpenChange={(open) => !open && setRevertTarget(null)}
      />
      <RecordEditDialog
        tableName={tableName}
        row={editRow}
        columns={schema?.columns ?? []}
        naturalKey={schema?.natural_key ?? []}
        onOpenChange={(open) => !open && setEditRow(null)}
        onStaged={onStaged}
      />
      <div className="px-4 py-3 flex justify-between items-center border-t border-[#f2f4f7] font-mono text-[11px] text-[#7b8799]">
        <span>
          {t("Showing {{from}}–{{to}} of {{total}}", {
            from: total === 0 ? 0 : page * PAGE_SIZE + 1,
            to: Math.min((page + 1) * PAGE_SIZE, total),
            total: total.toLocaleString(),
          })}
        </span>
        <div className="flex gap-1.5 items-center">
          <button
            className="h-7 px-2.5 rounded-md border border-[#e8ebf0] disabled:opacity-40 hover:border-[#08254c]"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            {t("Prev")}
          </button>
          <span className="px-2">
            {page + 1} / {pageCount}
          </span>
          <button
            className="h-7 px-2.5 rounded-md border border-[#e8ebf0] disabled:opacity-40 hover:border-[#08254c]"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("Next")}
          </button>
        </div>
      </div>
    </Card>
  );
}
